// node_modules
import React, {useState, useEffect, useContext, useRef} from 'react'
import {
  format,
  parse,
  addHours,
  compareAsc,
  isAfter,
  isBefore,
  addDays,
  startOfWeek as startOfWeekFn,
  isSameDay
} from "date-fns";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import { useDispatch } from "react-redux";
import {useMobile} from '../../../hooks/useMobile'

// redux
import {
  useGetScheduleListQuery,
  useCreateScheduleMutation,
  useUpdateScheduleMutation,
} from "../../../redux/services/schedule";
import { useGetStationListQuery } from "../../../redux/services/foodStation";

// primereact components
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";

// context
import { ToastContext } from "../../../App";
import {setLoading, setUnsavedChanges} from '../../../redux/slices/app.slice'

// utils
import getSubdomain from "../../../utils/getSubdomain";

// styles
import * as styles from "./Scheduling.module.css";

const Scheduling = () => {
  const showToast = useContext(ToastContext);
  const dispatch = useDispatch();
  const {isMobile} = useMobile();
  const draggingElRef = useRef(null);
  const calendarRef = useRef(null)
  const [selectedDate, setSelectedDate] = useState(Date.now());

  const {
    data: scheduling,
    isSuccess: isGetSchedulingSuccess,
    isError: isGetSchedulingError,
  } = useGetScheduleListQuery(getSubdomain());
  const [
    createSchedule,
    {
      isSuccess: isCreateScheduleSuccess,
      isError: isCreateScheduleError,
      error: createScheduleError,
    },
  ] = useCreateScheduleMutation();
  const [
    updateSchedule,
    {
      isSuccess: isUpdateScheduleSuccess,
      isError: isUpdateScheduleError,
      error: updateScheduleError,
    },
  ] = useUpdateScheduleMutation();
  const {
    data: stations,
    isSuccess: isGetStationsSuccess,
    isError: isGetStationsError,
  } = useGetStationListQuery(getSubdomain());

  const [isDragging, setIsDragging] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);
  const [slots, setSlots] = useState({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  });

  useEffect(() => {
    if (isCreateScheduleSuccess) {
      showToast("success", "Schedule", "Schedule created successfully");
      setIsDirty(false);
      dispatch(setUnsavedChanges(false))
    }
  }, [isCreateScheduleSuccess]);

  useEffect(() => {
    if (isUpdateScheduleSuccess) {
      showToast("success", "Schedule", "Schedule updated successfully");
      setIsDirty(false);
      dispatch(setUnsavedChanges(false))
    }
  }, [isUpdateScheduleSuccess]);
  
  useEffect(() => {
    if(isGetSchedulingSuccess)
      dispatch(setLoading(false));
  }, [isGetSchedulingSuccess])
  
  useEffect(() => {
    if(isGetSchedulingError) {
      showToast('error', 'Schedule', 'Unable to load schedule data')
      dispatch(setLoading(false));
    }
  }, [isGetSchedulingError])
  
  useEffect(() => {
    if(isCreateScheduleError)
      showToast("error", createScheduleError?.status, createScheduleError?.data?.message || createScheduleError?.data?.errors && "Incorrect data");
  }, [isCreateScheduleError])
  
  useEffect(() => {
    if(isUpdateScheduleError)
      showToast("error", updateScheduleError?.status, updateScheduleError?.data?.message || updateScheduleError?.data?.errors && "Incorrect data");
  }, [isUpdateScheduleError])
  
  useEffect(()=>{
    if(isGetStationsError) {
      showToast('error', 'Station', 'Unable to load stations')
      dispatch(setLoading(false));
    }
  }, [isGetStationsError])

  useEffect(() => {
    if (isGetSchedulingSuccess && selectedStation) {
      let draggableEl = document.getElementById("mydraggable");
      const dr = new Draggable(draggableEl);
      const calendarSlots = {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
      };

      const schedule = scheduling.find(
        (item) => item.food_station_id === selectedStation.id
      );

      if (schedule) {
        schedule.days.forEach((item) => {
          switch (item.day) {
            case "sunday":
              item.slots.forEach((slot, index) => {
                calendarSlots.sunday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [0],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [0] },
                });
              });
              break;
            case "monday":
              item.slots.forEach((slot, index) => {
                calendarSlots.monday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [1],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [1] },
                });
              });
              break;
            case "tuesday":
              item.slots.forEach((slot, index) => {
                calendarSlots.tuesday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [2],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [2] },
                });
              });
              break;
            case "wednesday":
              item.slots.forEach((slot, index) => {
                calendarSlots.wednesday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [3],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [3] },
                });
              });
              break;
            case "thursday":
              item.slots.forEach((slot, index) => {
                calendarSlots.thursday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [4],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [4] },
                });
              });
              break;
            case "friday":
              item.slots.forEach((slot, index) => {
                calendarSlots.friday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [5],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [5] },
                });
              });
              break;
            case "saturday":
              item.slots.forEach((slot, index) => {
                calendarSlots.saturday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [6],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [6] },
                });
              });
              break;
          }
        });
      }

      setSlots(calendarSlots);
      setIsDirty(false);
      dispatch(setUnsavedChanges(false))
    }
  }, [selectedStation, isGetSchedulingSuccess]);

  const saveSchedule = () => {
    const schedule = scheduling.find(
      (item) => item.food_station_id === selectedStation.id
    );

    const payload = {
      days: [],
    };

    Object.keys(slots).forEach((day) => {
      const oneDay = { day, slots: [] };
      slots[day].forEach((slot) => {
        oneDay.slots.push({ start: slot.startTime, end: slot.endTime });
      });
      if (oneDay.slots.length) {
        payload.days.push(oneDay);
      }
    });

    if (schedule) {
      console.log("saved", payload);
      updateSchedule({
        id: schedule.id,
        payload,
        subDomain: getSubdomain(),
      });
    } else {
      console.log("created", slots);
      payload.food_station_id = selectedStation.id;
      payload.subdomain = getSubdomain();
      createSchedule(payload);
    }
  };

  const WeekdaySelector = ({ selectedDate, onChange }) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const startOfWeek = startOfWeekFn(selectedDate)
    
    return (
      <div className={styles.weekdaySwitcher}>
        {days.map((day, i) => {
          const date = addDays(startOfWeek, i)
          const isSelected = isSameDay(date, selectedDate)
          
          return (
            <button
              key={day}
              onClick={() => onChange(date)}
              className={`${styles.weekdayBtn} ${isSelected ? `${styles.selected}` : ''}`}
            >
              <span>{day.toUpperCase()}</span>
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.pageHeader}>
        <h2>Schedule</h2>
        {isGetStationsSuccess && (
          <Dropdown
            value={selectedStation}
            onChange={(e) => setSelectedStation(e.value)}
            options={stations?.toSorted((a, b) =>
              a.name.localeCompare(b.name)
            )}
            optionLabel="name"
            placeholder="Select your station"
            className={`${styles.dropdown}`}
          />
        )}
      </div>
      {isMobile && selectedStation && (
        <WeekdaySelector
          selectedDate={selectedDate}
          onChange={(date) => {
            setSelectedDate(date)
            calendarRef.current.getApi().gotoDate(date)
          }}
        />
      )}
      <div className={styles.actions}>
        {selectedStation && (
          <div className={styles.createSlot}>
            {isMobile && <span>Long Press + drag to create a slot</span>}
            <div className={styles.dragCreateBtn} id="mydraggable">
              <i className="pi pi-plus" /><span>{" DRAG TO ADD SLOT"}</span>
            </div>
          </div>
        )}
        {isDragging && (
          <div id="delete_event" className={styles.deleteSlot}>
            <i className='pi pi-trash mr-3'/>
            <span>DROP HERE TO DELETE</span>
          </div>
        )}
        <div className={styles.saveBtnWrapper}>
          {" "}
          {isDirty && (
            <Button className={styles.saveBtn} onClick={saveSchedule}>
              <span>SAVE SCHEDULE</span><i className="pi pi-check-square"></i>
            </Button>
          )}
        </div>
      </div>

      {isGetSchedulingSuccess && (
        <>
          {selectedStation ? (
            <div className={styles.calendarWrapper}>
                  <FullCalendar
                    key={isMobile ? 'mobile' : 'desktop'}
                    ref={calendarRef}
                    plugins={[timeGridPlugin, interactionPlugin]}
                    initialView={isMobile ? "timeGridDay" : "timeGridWeek"}
                    dayHeaderFormat={isMobile ? false : { weekday: "long" }}
                    slotDuration="00:15:00"
                    slotMinTime="00:00:00"
                    slotMaxTime="23:59:00"
                    allDaySlot={false}
                    headerToolbar={false}
                    slotLabelInterval="01:00:00"
                    scrollTime={"06:00:00"}
                    eventTimeFormat={{
                      hour: "2-digit",
                      minute: "2-digit",
                      meridiem: true,
                    }}
                    eventOverlap={false}
                    events={Object.values(slots).reduce(
                      (prev, curr) => [...prev, ...curr],
                      []
                    )}
                    eventColor={isMobile ? "#58A4A4" : "#94BCE6"}
                    locale=""
                    height={700}
                    droppable={true}
                    eventShortHeight={60}
                    drop={(e) => {
                      console.log("dropped", e);
                      const startTime = format(e.date, "HH:mm");
                      const endTime = format(addHours(e.date, 1), "HH:mm");
                      
                      const event = {
                        title: "",
                        daysOfWeek: [],
                        startTime: startTime,
                        endTime: endTime,
                        constraint: { daysOfWeek: [] },
                      };
                      
                      const eventDay = format(e.date, "EEEE").toLowerCase();
                      
                      if (slots[eventDay].length === 4) {
                        return;
                      }
                      
                      let isOverlapping = false;
                      
                      slots[eventDay].forEach((slot) => {
                        if (
                          isAfter(
                            parse(startTime, "HH:mm", new Date()),
                            parse(slot.startTime, "HH:mm", new Date())
                          ) &&
                          isBefore(
                            parse(endTime, "HH:mm", new Date()),
                            parse(slot.endTime, "HH:mm", new Date())
                          )
                        ) {
                          isOverlapping = true;
                          return;
                        }
                      });
                      
                      if (isOverlapping) {
                        return;
                      }
                      
                      switch (eventDay) {
                        case "sunday":
                          event.daysOfWeek.push(0);
                          event.constraint.daysOfWeek.push(0);
                          break;
                        case "monday":
                          event.daysOfWeek.push(1);
                          event.constraint.daysOfWeek.push(1);
                          break;
                        case "tuesday":
                          event.daysOfWeek.push(2);
                          event.constraint.daysOfWeek.push(2);
                          break;
                        case "wednesday":
                          event.daysOfWeek.push(3);
                          event.constraint.daysOfWeek.push(3);
                          break;
                        case "thursday":
                          event.daysOfWeek.push(4);
                          event.constraint.daysOfWeek.push(4);
                          break;
                        case "friday":
                          event.daysOfWeek.push(5);
                          event.constraint.daysOfWeek.push(5);
                          break;
                        case "saturday":
                          event.daysOfWeek.push(6);
                          event.constraint.daysOfWeek.push(6);
                          break;
                      }
                      
                      const newSameDayEvents = [...slots[eventDay], event]
                        .sort((a, b) =>
                          compareAsc(
                            parse(a.startTime, "HH:mm", new Date()),
                            parse(b.startTime, "HH:mm", new Date())
                          )
                        )
                        .map((slot, index) => ({
                          ...slot,
                          title: `Slot ${index + 1}`,
                        }));
                      
                      setSlots((prevSlots) => ({
                        ...prevSlots,
                        [eventDay]: newSameDayEvents,
                      }));
                      setIsDirty(true);
                      dispatch(setUnsavedChanges(true))
                    }}
                    eventDragStart={(info) => {
                      const el = info.el
                      draggingElRef.current = el
                      el.classList.add('drag-resize')
                      setIsDragging(true)
                    }}
                    eventDragStop={(info) => {
                      if (info.jsEvent.target.id === "delete_event") {
                        const eventDayName = format(
                          info.event.start,
                          "EEEE"
                        ).toLowerCase();
                        
                        setSlots((prev) => ({
                          ...prev,
                          [eventDayName]: [
                            ...slots[eventDayName]
                              .filter((slot) => slot.title !== info.event.title)
                              .map((item, index) => ({
                                ...item,
                                title: `Slot ${index + 1}`,
                              })),
                          ],
                        }));
                        setIsDirty(true);
                        dispatch(setUnsavedChanges(true))
                      }
                      const el = info.el
                      el.classList.remove('drag-resize')
                      draggingElRef.current = null
                      setIsDragging(false);
                    }}
                    eventChange={(info) => {
                      const newStart = format(info.event.start, "HH:mm");
                      const newEnd = format(info.event.end, "HH:mm");
                      
                      // Prevent zero-length or reversed slot
                      if (
                        newStart === newEnd ||
                        isAfter(
                          parse(newStart, "HH:mm", new Date()),
                          parse(newEnd, "HH:mm", new Date())
                        )
                      ) {
                        info.revert();
                        return;
                      }
                      
                      const eventDayName = format(
                        info.event.start,
                        "EEEE"
                      ).toLowerCase();
                      
                      const sameDayEvents = slots[eventDayName]
                        .map((slot) => {
                          if (
                            slot.startTime === format(info.oldEvent.start, "HH:mm")
                          ) {
                            return {
                              ...slot,
                              startTime: format(info.event.start, "HH:mm"),
                              endTime: format(info.event.end, "HH:mm"),
                            };
                          }
                          return slot;
                        })
                        .toSorted((a, b) =>
                          compareAsc(
                            parse(a.startTime, "HH:mm", new Date()),
                            parse(b.startTime, "HH:mm", new Date())
                          )
                        )
                        .map((slot, index) => {
                          return { ...slot, title: `Slot ${index + 1}` };
                        });
                      
                      setSlots((prev) => ({
                        ...prev,
                        [eventDayName]: sameDayEvents,
                      }));
                      setIsDirty(true);
                      dispatch(setUnsavedChanges(true))
                    }}
                    // Interactive plugin configurations
                    editable={true}
                    eventStartEditable={true}
                    eventDurationEditable={true}
                    eventResizableFromStart={true} // Allow resizing from start and end
                    snapDuration="00:15:00" // Snap to 15-minute intervals when resizing
                  />
            </div>
          ) : (
            <div>Select station please</div>
          )}
        </>
      )}
    </div>
  );
};

export default Scheduling;
